var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGetResourceLabel, useTranslate, useResourceContext, useNotify, } from 'react-admin';
import cloneDeep from 'lodash/cloneDeep';
import { useGetChildNodesCallback, useGetRootNodes, useGetTree, useGetTreeCallback, useMoveAsNthChildOf, useMoveAsNthSiblingOf, } from '../dataProvider';
import { getRCTree, useExpandedKeys } from '../util';
import { UNSAVED_NEW_NODE } from '../constants';
export var useTreeController = function (options) {
    var translate = useTranslate();
    var resource = useResourceContext(options);
    var getResourceLabel = useGetResourceLabel();
    var hideRootNodes = options.hideRootNodes, _a = options.lazy, lazy = _a === void 0 ? false : _a, _b = options.titleField, titleField = _b === void 0 ? 'title' : _b, _c = options.mutationMode, mutationMode = _c === void 0 ? 'undoable' : _c, meta = options.meta;
    // Node creation support
    var _d = useState(), newNode = _d[0], setNewNode = _d[1];
    var handleAddNode = useCallback(function (newNode) {
        setNewNode(newNode);
    }, []);
    // load entire tree on mount if lazy loading is not enabled
    var treeState = useGetTree(resource, { meta: meta }, { enabled: !lazy });
    // load only the root nodes on mount if lazy loading is enabled
    var rootNodesState = useGetRootNodes(resource, { meta: meta }, { enabled: lazy });
    var innerLoadChildNodes = useGetChildNodesCallback(resource, {
        meta: meta,
    });
    var _e = lazy
        ? rootNodesState
        : treeState, data = _e.data, error = _e.error, isLoading = _e.isLoading, isFetching = _e.isFetching;
    var _f = useState(data), finalData = _f[0], setFinalData = _f[1];
    var loadChildNodes = useCallback(function (parentId) { return __awaiter(void 0, void 0, void 0, function () {
        var records;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, innerLoadChildNodes(parentId)];
                case 1:
                    records = _a.sent();
                    setFinalData(function (previous) { return __spreadArray(__spreadArray([], previous, true), records, true); });
                    return [2 /*return*/];
            }
        });
    }); }, [innerLoadChildNodes]);
    useEffect(function () {
        var hasNewNode = newNode != null &&
            !data.some(function (record) { return record.id === UNSAVED_NEW_NODE; });
        if (hasNewNode) {
            var newNodes = cloneDeep(data);
            if (newNode.parentId != null) {
                var parentNode = newNodes.find(function (node) { return node.id === newNode.parentId; });
                if (parentNode) {
                    parentNode.children = parentNode.children || [];
                    parentNode.children.push(UNSAVED_NEW_NODE);
                }
            }
            setFinalData(__spreadArray(__spreadArray([], newNodes, true), [
                __assign({ id: UNSAVED_NEW_NODE, children: [] }, newNode.record),
            ], false));
        }
        else {
            setFinalData(data);
        }
    }, [newNode, data]);
    // prepare data structure for rc-tree
    // add key and title, and turn to recursive DataNode[] format
    var tree = useMemo(function () {
        return finalData
            ? getRCTree(finalData, titleField, translate('ra-tree.new_node'), hideRootNodes)
            : null;
    }, [finalData, hideRootNodes, titleField, translate]);
    var _g = useExpandedKeys(), expandedKeys = _g[0], handleExpand = _g[1];
    var expandedNodesLoaded = useRef(false);
    // for some reason, rc-tree does not call loadData on all the expanded nodes in lazy mode
    // so we need to do it manually
    useEffect(function () {
        if (lazy && !expandedNodesLoaded.current) {
            expandedKeys.forEach(function (expandedKey) {
                loadChildNodes(expandedKey);
            });
            expandedNodesLoaded.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expandedKeys, lazy]);
    // prepare hooks for drag and drop
    var moveAsNthChildOf = useMoveAsNthChildOf(resource, { meta: meta })[0];
    var moveAsNthSiblingOf = useMoveAsNthSiblingOf(resource, { meta: meta })[0];
    var refreshTree = useGetTreeCallback(resource);
    var notify = useNotify();
    /**
     * Call dataProvider.moveXXX() method on drop.
     *
     * Heavily inspired by https://github.com/react-component/tree/blob/master/examples/draggable.jsx
     *
     * @see https://github.com/react-component/tree/blob/master/examples/draggable.jsx
     */
    var handleDrop = useCallback(function (info) {
        var dropKey = info.node.key;
        var dragKey = info.dragNode.key;
        var dropPos = info.node.pos.split('-');
        var dropPosition = info.dropPosition - Number(dropPos[dropPos.length - 1]);
        var dataById = data.reduce(function (acc, curr) {
            acc[curr.id] = curr;
            return acc;
        }, {});
        var dragObject = dataById[dragKey];
        var targetObject = dataById[dropKey];
        // refresh tree on success
        var successSideEffect = function () { return __awaiter(void 0, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, refreshTree()];
                    case 1:
                        _a.sent();
                        notify('ra-tree.item_moved', {
                            undoable: mutationMode === 'undoable',
                        });
                        return [2 /*return*/];
                }
            });
        }); };
        if (!info.dropToGap) {
            // Drop on a node
            // move dragObject as first child of targetObject
            moveAsNthChildOf(resource, {
                source: dragObject,
                destination: targetObject,
                position: 0,
            }, { onSuccess: successSideEffect, mutationMode: mutationMode });
        }
        else if ((info.node.children || []).length > 0 && // Has children
            info.node.expanded && // Is expanded
            dropPosition === 1 // On the bottom gap
        ) {
            // drop on the top gap
            // move dragObject as last child of targetObject
            moveAsNthChildOf(resource, {
                source: dragObject,
                destination: targetObject,
                position: targetObject.children.length,
            }, { onSuccess: successSideEffect, mutationMode: mutationMode });
        }
        else {
            // drop between nodes
            // look for the target position
            var targetPosition_1;
            var loop_1 = function (nodes, key, callback) {
                nodes.forEach(function (item, index) {
                    if (item.key === key) {
                        callback(index);
                        return;
                    }
                    if (item.children) {
                        loop_1(item.children, key, callback);
                    }
                });
            };
            loop_1(tree, dropKey, function (index) {
                targetPosition_1 = index;
            });
            // move dragObject to sibling targetPosition of targetObject
            moveAsNthSiblingOf(resource, {
                source: dragObject,
                destination: targetObject,
                position: dropPosition === -1
                    ? targetPosition_1
                    : targetPosition_1 + 1,
            }, { onSuccess: successSideEffect, mutationMode: mutationMode });
        }
    }, [
        data,
        moveAsNthChildOf,
        moveAsNthSiblingOf,
        mutationMode,
        notify,
        refreshTree,
        resource,
        tree,
    ]);
    var defaultTitle = translate('ra.page.list', {
        name: getResourceLabel(resource, 2),
    });
    return {
        data: finalData,
        defaultTitle: defaultTitle,
        error: error,
        expandedKeys: expandedKeys,
        handleAddNode: handleAddNode,
        handleDrop: handleDrop,
        handleExpand: handleExpand,
        isLoading: isLoading,
        isFetching: isFetching,
        loadChildNodes: loadChildNodes,
        newNode: newNode,
        tree: tree,
    };
};
